import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    HeartHandshake,
    Pin,
    Edit3,
    Trash2,
    Send,
    Bell,
    BellOff,
    Loader2,
    Image as ImageIcon,
    Video as VideoIcon,
    CheckCircle2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import useDraft from "../../../hooks/useDraft";
import SelfieRecorder from "../../SelfieRecorder";
import FileUpload from "../../FileUpload";
import { useConfirm } from "../../ConfirmDialog";

/**
 * Iter-27.68 — Pattern "Composer inline" pour les remerciements.
 *
 * Plus de modal qui se déborde et qu'on n'arrive pas à fermer. À la place,
 * une vraie zone d'écriture style Twitter/LinkedIn TOUJOURS visible en haut
 * de l'onglet. Les messages existants s'affichent en cartes en-dessous.
 *
 * États du composer :
 *   - mode "create" (par défaut) : champs vierges, bouton "Publier"
 *   - mode "edit" : champs pré-remplis, bouton "Mettre à jour" + "Annuler"
 *
 * Plus de bouton "+ Nouveau remerciement" séparé : le composer EST le mode
 * de création par défaut.
 */
export default function GratitudeMessagesTab({ assoId, useAssoEndpoints = false, onChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composer, setComposer] = useState(initialComposer());
    const [saving, setSaving] = useState(false);
    const composerRef = useRef(null);
    const confirm = useConfirm();

    // Iter-27.69 — Brouillon automatique localStorage (clé scopée par asso)
    const draftKey = `shariti.draft.gratitude.${assoId || "self"}`;
    const { clear: clearDraft } = useDraft(draftKey, composer, setComposer, {
        isInitial: (s) =>
            !s._editingId && !s.title?.trim() && !s.body?.trim() && !s.image_url,
        onRestored: () =>
            toast.info("✏️ Brouillon récupéré", { duration: 3000 }),
    });

    // Endpoints selon le rôle (admin Shariti vs compte asso)
    const listUrl = useAssoEndpoints
        ? "/asso/news"
        : `/admin/associations/${assoId}/news`;
    const createUrl = listUrl;
    const updateUrl = (nid) =>
        useAssoEndpoints
            ? `/asso/news/${nid}`
            : `/admin/associations/${assoId}/news/${nid}`;
    const deleteUrl = updateUrl;

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get(listUrl);
            const onlyGratitude = (r.data || []).filter((n) => n.category === "gratitude");
            setItems(onlyGratitude);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assoId, useAssoEndpoints]);

    const startEdit = (item) => {
        setComposer({
            _editingId: item.news_id,
            title: item.title || "",
            body: item.body || "",
            image_url: item.image_url || "",
            pinned: !!item.pinned,
            notify_push: false, // pas de notify sur update
        });
        // Scroll vers le composer pour donner du feedback
        composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => setComposer(initialComposer());

    const submit = async () => {
        if (!composer.title.trim() || !composer.body.trim()) {
            toast.error("Titre et message sont requis");
            return;
        }
        setSaving(true);
        try {
            if (composer._editingId) {
                await api.patch(updateUrl(composer._editingId), {
                    title: composer.title,
                    body: composer.body,
                    category: "gratitude",
                    pinned: !!composer.pinned,
                    image_url: composer.image_url || null,
                });
                toast.success("Message mis à jour");
            } else {
                await api.post(createUrl, {
                    title: composer.title,
                    body: composer.body,
                    category: "gratitude",
                    pinned: !!composer.pinned,
                    notify_push: !!composer.notify_push,
                    image_url: composer.image_url || null,
                });
                toast.success(
                    composer.notify_push
                        ? "Message publié + ambassadeurs notifiés"
                        : "Message publié",
                );
            }
            setComposer(initialComposer());
            clearDraft();
            await reload();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (nid) => {
        // Iter-27.101 — Modal de confirmation premium aligné design Shariti
        // (au lieu du window.confirm() natif basique).
        const item = items.find((it) => it.news_id === nid);
        const ok = await confirm({
            title: "Supprimer ce remerciement ?",
            description: item?.title
                ? `« ${item.title} » sera retiré de l'espace ambassadeurs. Cette action est irréversible.`
                : "Ce message de remerciement sera retiré de l'espace ambassadeurs. Cette action est irréversible.",
            confirmLabel: "Supprimer",
            cancelLabel: "Annuler",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(deleteUrl(nid));
            toast.success("Message supprimé");
            // Si on était en train d'éditer ce même message → reset
            if (composer._editingId === nid) {
                setComposer(initialComposer());
                clearDraft();
            }
            await reload();
            onChanged?.();
        } catch {
            toast.error("Erreur");
        }
    };

    const isEditing = !!composer._editingId;
    const canSubmit = composer.title.trim() && composer.body.trim() && !saving;

    return (
        <div className="space-y-6">
            {/* COMPOSER INLINE — toujours visible en haut */}
            <motion.div
                ref={composerRef}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="gratitude-composer"
                className={`relative rounded-3xl border p-5 sm:p-6 transition-all ${
                    isEditing
                        ? "bg-shariti-yellow/8 border-shariti-yellow/40 ring-2 ring-shariti-yellow/30"
                        : "bg-white/3 border-white/10"
                }`}
            >
                {/* Header composer */}
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-2xl bg-shariti-yellow/20 flex items-center justify-center shrink-0">
                        <HeartHandshake className="w-5 h-5 text-shariti-yellow" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow">
                            {isEditing ? "Modifier ce message" : "Adresse un message à tes ambassadeurs"}
                        </p>
                        <p className="text-[11px] text-white/55 font-light mt-0.5">
                            {isEditing
                                ? "Tu peux modifier le contenu — il sera mis à jour dans les espaces ambassadeurs."
                                : "Un message authentique, court et personnel. Ajoute une mini-vidéo selfie ou une photo pour plus d'impact."}
                        </p>
                    </div>
                    {isEditing && (
                        <button
                            onClick={cancelEdit}
                            data-testid="gratitude-composer-cancel"
                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-white/60 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5 transition-all"
                        >
                            <X className="w-3.5 h-3.5" />
                            Annuler
                        </button>
                    )}
                </div>

                <div className="space-y-3">
                    {/* Titre */}
                    <input
                        type="text"
                        value={composer.title}
                        onChange={(e) =>
                            setComposer({ ...composer, title: e.target.value })
                        }
                        placeholder="Titre du remerciement (ex : Merci pour ton engagement)"
                        maxLength={200}
                        data-testid="gratitude-composer-title"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm sm:text-base font-bold outline-none focus:border-shariti-yellow/40 placeholder:font-normal placeholder:text-white/35"
                    />

                    {/* Body */}
                    <textarea
                        rows={4}
                        value={composer.body}
                        onChange={(e) =>
                            setComposer({ ...composer, body: e.target.value })
                        }
                        placeholder="Adresse un message authentique aux ambassadeurs : ce qu'ils apportent au terrain, l'impact qu'ils créent, une victoire récente…"
                        data-testid="gratitude-composer-body"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-yellow/40 resize-y placeholder:text-white/35"
                    />

                    {/* Média : SelfieRecorder OU FileUpload + Preview */}
                    {composer.image_url ? (
                        <div
                            data-testid="gratitude-composer-media-preview"
                            className="relative rounded-2xl overflow-hidden border border-shariti-yellow/30 bg-black"
                        >
                            {/\.(mp4|webm|mov)(\?|$)/i.test(composer.image_url) ? (
                                <video
                                    src={composer.image_url}
                                    controls
                                    playsInline
                                    className="w-full max-h-72 object-contain bg-black"
                                />
                            ) : (
                                <img
                                    src={composer.image_url}
                                    alt=""
                                    className="w-full max-h-72 object-cover"
                                />
                            )}
                            <button
                                onClick={() => setComposer({ ...composer, image_url: "" })}
                                data-testid="gratitude-composer-media-remove"
                                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/70 hover:bg-red-500/90 text-white text-[11px] font-bold backdrop-blur transition-all"
                            >
                                <Trash2 className="w-3 h-3" />
                                Retirer
                            </button>
                        </div>
                    ) : (
                        <MediaPicker
                            onUpload={(url) => setComposer({ ...composer, image_url: url })}
                        />
                    )}

                    {/* Toggles bar */}
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
                        <ToggleChip
                            active={composer.pinned}
                            onToggle={() =>
                                setComposer({ ...composer, pinned: !composer.pinned })
                            }
                            icon={Pin}
                            label="Épingler en haut"
                            color="#FCDB53"
                            testId="gratitude-composer-pinned"
                        />
                        {!isEditing && (
                            <ToggleChip
                                active={composer.notify_push}
                                onToggle={() =>
                                    setComposer({
                                        ...composer,
                                        notify_push: !composer.notify_push,
                                    })
                                }
                                icon={composer.notify_push ? Bell : BellOff}
                                label="Notifier les ambassadeurs"
                                color="#EE722E"
                                testId="gratitude-composer-notify"
                            />
                        )}

                        {/* Submit button → push à droite */}
                        <button
                            onClick={submit}
                            disabled={!canSubmit}
                            data-testid="gratitude-composer-submit"
                            className="ml-auto inline-flex items-center gap-2 bg-shariti-yellow hover:bg-shariti-yellow/90 text-shariti-black font-extrabold px-5 py-2.5 rounded-2xl text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isEditing ? (
                                <CheckCircle2 className="w-4 h-4" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            {isEditing
                                ? "Mettre à jour"
                                : composer.notify_push
                                  ? "Publier + Notifier"
                                  : "Publier"}
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* LISTE des messages déjà publiés */}
            <div>
                <p className="text-[11px] font-thin tracking-[0.2em] uppercase text-white/55 mb-3">
                    {items.length} message{items.length > 1 ? "s" : ""} publié
                    {items.length > 1 ? "s" : ""}
                </p>

                {loading ? (
                    <p className="text-white/40 text-sm py-6 text-center">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                    </p>
                ) : items.length === 0 ? (
                    <div
                        className="rounded-3xl border border-dashed border-white/10 p-10 text-center"
                        data-testid="gratitude-empty"
                    >
                        <HeartHandshake className="w-10 h-10 mx-auto mb-2 text-shariti-yellow/40" />
                        <p className="text-sm text-white/65 font-bold">
                            Aucun remerciement publié.
                        </p>
                        <p className="text-xs text-white/45 mt-1">
                            Compose ton premier message dans la zone ci-dessus.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        <AnimatePresence>
                            {items.map((n) => {
                                const isCurrentlyEditing = composer._editingId === n.news_id;
                                const hasVideo =
                                    n.image_url && /\.(mp4|webm|mov)(\?|$)/i.test(n.image_url);
                                const hasImage = n.image_url && !hasVideo;
                                return (
                                    <motion.div
                                        key={n.news_id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        layout
                                        data-testid={`gratitude-row-${n.news_id}`}
                                        className={`flex items-start gap-3 p-4 rounded-2xl border transition-all ${
                                            isCurrentlyEditing
                                                ? "bg-shariti-yellow/10 border-shariti-yellow/40"
                                                : "bg-white/3 border-white/10 hover:border-white/20"
                                        }`}
                                    >
                                        {/* Thumbnail média ou icon */}
                                        {hasImage ? (
                                            <img
                                                src={n.image_url}
                                                alt=""
                                                className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover shrink-0"
                                            />
                                        ) : hasVideo ? (
                                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-black/60 flex items-center justify-center shrink-0 border border-shariti-yellow/30">
                                                <VideoIcon className="w-6 h-6 text-shariti-yellow" />
                                            </div>
                                        ) : (
                                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-shariti-yellow/15 flex items-center justify-center shrink-0">
                                                <HeartHandshake className="w-6 h-6 text-shariti-yellow" />
                                            </div>
                                        )}

                                        {/* Contenu */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm sm:text-base font-extrabold text-white truncate">
                                                    {n.title}
                                                </p>
                                                {n.pinned && (
                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-wider text-shariti-yellow bg-shariti-yellow/15 px-1.5 py-0.5 rounded-full">
                                                        <Pin className="w-2.5 h-2.5" /> Épinglé
                                                    </span>
                                                )}
                                                {isCurrentlyEditing && (
                                                    <span className="text-[9px] font-extrabold uppercase tracking-wider text-shariti-yellow bg-shariti-yellow/20 px-1.5 py-0.5 rounded-full animate-pulse">
                                                        Édition en cours
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-white/60 line-clamp-2 mt-0.5 leading-relaxed">
                                                {n.body}
                                            </p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <button
                                                onClick={() => startEdit(n)}
                                                data-testid={`gratitude-edit-${n.news_id}`}
                                                aria-label="Modifier"
                                                className="p-2 rounded-lg bg-white/5 hover:bg-shariti-yellow/20 hover:text-shariti-yellow text-white/65 transition-all"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => remove(n.news_id)}
                                                data-testid={`gratitude-delete-${n.news_id}`}
                                                aria-label="Supprimer"
                                                className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/65 hover:text-red-300 transition-all"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
}


function initialComposer() {
    return {
        _editingId: null,
        title: "",
        body: "",
        image_url: "",
        pinned: false,
        notify_push: true,
    };
}


/**
 * MediaPicker : choix entre selfie recorder ou upload classique.
 * Onglet pill très discret pour ne pas surcharger le composer.
 */
function MediaPicker({ onUpload }) {
    const [mode, setMode] = useState("idle"); // "idle" | "selfie" | "upload"

    if (mode === "idle") {
        return (
            <div
                className="flex items-center gap-2 p-1 bg-white/3 border border-white/8 rounded-2xl"
                data-testid="gratitude-composer-media-picker"
            >
                <button
                    onClick={() => setMode("selfie")}
                    data-testid="gratitude-composer-media-selfie"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-white/70 hover:text-shariti-yellow hover:bg-shariti-yellow/10 transition-all"
                >
                    <VideoIcon className="w-3.5 h-3.5" />
                    Enregistrer une vidéo selfie
                </button>
                <button
                    onClick={() => setMode("upload")}
                    data-testid="gratitude-composer-media-upload"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-white/70 hover:text-shariti-orange-light hover:bg-shariti-orange/10 transition-all"
                >
                    <ImageIcon className="w-3.5 h-3.5" />
                    Uploader image / vidéo
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <p className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/45">
                    {mode === "selfie" ? "Enregistrement selfie" : "Upload média"}
                </p>
                <button
                    onClick={() => setMode("idle")}
                    className="text-[11px] text-white/55 hover:text-white font-bold inline-flex items-center gap-1"
                >
                    <X className="w-3 h-3" />
                    Annuler
                </button>
            </div>
            {mode === "selfie" ? (
                <SelfieRecorder
                    testIdPrefix="gratitude-selfie"
                    maxSeconds={60}
                    onUploaded={(url) => {
                        onUpload(url);
                        setMode("idle");
                    }}
                />
            ) : (
                <FileUpload
                    kind="image"
                    currentUrl=""
                    onUploaded={(url) => {
                        onUpload(url);
                        setMode("idle");
                    }}
                    testId="gratitude-media-upload"
                />
            )}
        </div>
    );
}


function ToggleChip({ active, onToggle, icon: Icon, label, color, testId }) {
    return (
        <button
            onClick={onToggle}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                active
                    ? "text-shariti-black"
                    : "text-white/55 border-white/10 hover:text-white"
            }`}
            style={
                active
                    ? { background: color, borderColor: color }
                    : {}
            }
        >
            <Icon className="w-3 h-3" />
            {label}
        </button>
    );
}
